import Seo from "@/components/Seo";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Globe2, RefreshCw, ArrowRight } from "lucide-react";
import api from "@/lib/api";

const VALUES = [
  { icon: ShieldCheck, title: "Verified Information", desc: "We source from official channels and link to original sources." },
  { icon: Globe2, title: "Global Coverage", desc: "AI is a global industry. Our platform serves users in every country." },
  { icon: RefreshCw, title: "Always Current", desc: "Our news and jobs update automatically so you always see what is happening right now." },
];

export default function About() {
  const [stats, setStats] = useState({ tools: 0, jobs: 0, countries: 0, articles: 0 });

  useEffect(() => {
    api.get("/tools").then((r) => setStats((s) => ({ ...s, tools: r.data.length }))).catch(() => {});
    api.get("/live-jobs").then((r) => setStats((s) => ({ ...s, jobs: r.data.total || 0, countries: (r.data.countries || []).length }))).catch(() => {});
    api.get("/articles").then((r) => setStats((s) => ({ ...s, articles: r.data.total || 0 }))).catch(() => {});
  }, []);

  const statCards = [
    ["Tools Listed", `${stats.tools}+`],
    ["Live Jobs", `${stats.jobs}+`],
    ["Countries Covered", `${stats.countries}+`],
    ["News Articles", `${stats.articles}+`],
    ["News Refresh", "Every 2h"],
  ];

  return (
    <div data-testid="about-page">
      <Seo title="About WorldAIInfo — The Global AI Intelligence Hub" description="WorldAIInfo brings together real, sourced data on the world of AI — tools, jobs, events, courses and country rankings. Learn about our mission." canonicalPath="/about" />
      <section className="grid-bg border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-20">
          <p className="font-mono-tech text-xs uppercase text-[#00F0FF] mb-3">Our Story</p>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl">About WorldAIInfo.com</h1>
          <p className="mt-6 text-base md:text-lg text-[#94A3B8] leading-relaxed">
            WorldAIInfo.com was built to solve a simple problem — the AI world moves faster than anyone can keep up with. We built one place where AI professionals, businesses, and curious people can find everything they need: the best tools, real jobs, live news, upcoming events, and quality courses — all in one platform, updated automatically, free to use.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {VALUES.map((v, i) => (
            <motion.div key={v.title} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.08 }}
              className="card-surface rounded-xl p-7" data-testid={`about-value-${i}`}>
              <div className="w-12 h-12 rounded-lg bg-[#00F0FF]/10 flex items-center justify-center mb-5">
                <v.icon className="w-6 h-6 text-[#00F0FF]" />
              </div>
              <h3 className="font-display font-bold text-xl mb-2">{v.title}</h3>
              <p className="text-[#94A3B8] text-sm leading-relaxed">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
        <div className="card-surface rounded-2xl p-10 grid grid-cols-2 md:grid-cols-5 gap-8" data-testid="about-stats">
          {statCards.map(([label, val]) => (
            <div key={label} className="text-center">
              <div className="font-display font-bold text-2xl md:text-3xl text-[#00F0FF]">{val}</div>
              <div className="font-mono-tech text-xs uppercase text-[#94A3B8] mt-1">{label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 md:px-12 pb-24 text-center">
        <h2 className="font-display font-bold text-2xl md:text-3xl mb-4">Have a question or want to work with us?</h2>
        <Link to="/contact" data-testid="about-contact-cta"
          className="inline-flex items-center gap-2 bg-[#00F0FF] text-[#04121a] font-semibold px-7 py-3 rounded-md hover:brightness-110 transition">
          Get in touch <ArrowRight className="w-4 h-4" />
        </Link>
      </section>
    </div>
  );
}
